import { useState, useRef, useEffect, useMemo } from "react";
import { useTranslate, useDataProvider, useNotify } from "react-admin";
import { CircularProgress, Typography, Autocomplete, Box } from "@mui/material";
import { debounce } from "lodash";

export const AutocompleteInput = ({
    value,
    setValue,
    resource,
    sort,
    labelKey,
    RenderInputComponent,
    renderOptionLabel,
    handleError,
    sx,
    filterBy,
    alwaysOn = true,
    helperText = "",
}) => {
    const PER_PAGE = 10;
    const notify = useNotify();
    const translate = useTranslate();
    const dataProvider = useDataProvider();
    const [isExhausted, setIsExhausted] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [data, setData] = useState([]);
    const inputRef = useRef(null);
    const initialized = useRef(false);
    const getData = (search, data) => {
        const page = Math.floor(data.length / PER_PAGE) + 1;
        dataProvider
            .getList(resource, {
                filter: {
                    [filterBy]: search,
                },
                pagination: { page, perPage: PER_PAGE },
                sort,
            })
            .then((response) => {
                setIsLoading(false);
                setData([...data, ...response.data]);
                if (response.data.length < PER_PAGE) {
                    setIsExhausted(true);
                }
            })
            .catch((e) => handleError(e, notify));
    };
    const getDataRef = useRef(getData);
    getDataRef.current = getData;

    const debouncedGetData = useMemo(
        () =>
            debounce((...args) => {
                getDataRef.current(...args);
            }, 300),
        []
    );

    useEffect(() => {
        return () => {
            debouncedGetData.cancel();
        };
    }, [debouncedGetData]);

    useEffect(() => {
        if (initialized.current) {
            return;
        }
        const searchValue = inputRef.current?.value;
        initialized.current = true;
        debouncedGetData(searchValue, data);
    }, [debouncedGetData, data]);

    return (
        <Autocomplete
            sx={sx}
            title={translate(labelKey)}
            label={translate(labelKey)}
            value={value}
            onInputChange={(event, value) => {
                setIsExhausted(false);
                debouncedGetData(value, []);
            }}
            onChange={(event, option) => {
                setValue(option);
            }}
            getOptionDisabled={(option) => !option.id}
            options={[...data, ...(isLoading ? [{}] : [])]}
            renderInput={(params) => (
                <RenderInputComponent
                    {...params}
                    label={translate(labelKey)}
                    inputRef={inputRef}
                />
            )}
            getOptionLabel={(params) => {
                if (typeof params === "string") return params;
                return renderOptionLabel(params);
            }}
            renderOption={(props, option) => {
                const { id } = option;
                const { key, ...rest } = props;
                const label = renderOptionLabel(option);
                return id ? (
                    <Box title={label} label={label} key={id} {...rest}>
                        <Box
                            key={id}
                            sx={{
                                p: 1,
                                m: 1
                            }}>
                            <Typography>{label}</Typography>
                        </Box>
                    </Box>
                ) : (
                    <Box
                        sx={{
                            mt: 2,
                            mb: 2,
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center"
                        }}>
                        <CircularProgress size={16} />
                        <Typography sx={{
                            ml: 1
                        }}>
                            {translate("equip.loading")}...
                        </Typography>
                    </Box>
                );
            }}
            slotProps={{
                listbox: {
                    onScroll: (event) => {
                        const listboxNode = event.currentTarget;
                        if (
                            listboxNode.scrollTop + listboxNode.clientHeight ===
                                listboxNode.scrollHeight &&
                            !isExhausted
                        ) {
                            const searchValue = inputRef.current?.value;
                            debouncedGetData(searchValue, data);
                        }
                    },
                }
            }}
        />
    );
};
